import React from 'react';
import { Box, Grid } from '@material-ui/core';
import {
  Button,
  Spinner,
  Typography,
  TypographyVariant,
} from 'components/core';
import { dayjs } from 'app/dayjs';
import { useBusiness } from 'hooks/business/use-business';
import { useBusinessMembers } from 'hooks/business/use-business-members';
import { useUser } from 'hooks/auth/use-user';
import { usePortfolios } from 'modules/portfolio/allocation/hooks/use-portfolios';
import { useBankAccounts } from 'hooks/banking/use-bank-accounts';
import { BankAccount } from 'types';
import { NotificationContainer } from 'modules/2023-q3/banking-page/v2/components/bank-accounts/components/bank/components/bank-plaid-error/components/notification-container/notification-container';
import { useTracking, TrackPageName } from 'modules/tracking';
import { RoutesPath } from 'routes/constants/routes-path';
import { useApprovalPolicy } from '../team/hooks/use-approval-policy';
import { Styled } from './approvals.style';
import {
  ApprovalRequest,
  ApprovalRequestPayload,
  useApprovalRequests,
  useApproveApprovalRequest,
  useCancelApprovalRequest,
} from './hooks/use-approval-requests';

interface BusinessMember {
  id?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
}

const WITHDRAWAL = 'WITHDRAWAL';
const REALLOCATION = 'REALLOCATION';
const NEW_ADMIN_MEMBER = 'NEW_ADMIN_MEMBER';
const ENABLE_MULTIPLE_APPROVAL = 'ENABLE_MULTIPLE_APPROVAL';
const DISABLE_MULTIPLE_APPROVAL = 'DISABLE_MULTIPLE_APPROVAL';

const usdFormat = (locales?: string | string[]) =>
  new Intl.NumberFormat(locales, {
    currency: 'USD',
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
    style: 'currency',
  });

// Integer cents stay exact. Dollar grouping and both digit styles come from
// the currency formatter's numbering system, so the fraction is not left in ASCII.
const centDigits = (cents: number, numberingSystem: string): string =>
  new Intl.NumberFormat(`en-u-nu-${numberingSystem}`, {
    maximumFractionDigits: 0,
    minimumIntegerDigits: 2,
    useGrouping: false,
  }).format(cents);

export const formatUsdFromCents = (
  amountCents: number,
  locales?: string | string[],
): string => {
  const cents = Math.round(amountCents);
  const negative = cents < 0;
  const abs = Math.abs(cents);
  const dollars = Math.floor(abs / 100);
  const whole = negative ? -dollars : dollars;
  const template = whole === 0 && negative ? -0.01 : whole;
  const currency = usdFormat(locales);
  const fraction = centDigits(
    abs % 100,
    currency.resolvedOptions().numberingSystem,
  );

  return currency
    .formatToParts(template)
    .map((part) => (part.type === 'fraction' ? fraction : part.value))
    .join('');
};

const withdrawalAmountLabel = (
  payload: ApprovalRequestPayload | null,
): string => {
  if (payload == null || payload.kind === 'other') {
    return 'Withdrawal';
  }

  if (payload.amountCents !== undefined) {
    return `Withdrawal of ${formatUsdFromCents(payload.amountCents)} USD`;
  }

  if (payload.kind === 'schedule_out') {
    const amountDollars = payload.body?.amountDollars;

    return amountDollars === undefined
      ? 'Withdrawal'
      : `Withdrawal of ${formatUsdFromCents(
          Math.round(amountDollars * 100),
        )} USD`;
  }

  const amountCents = payload.body?.amountCents;

  return amountCents === undefined
    ? 'Withdrawal'
    : `Withdrawal of ${formatUsdFromCents(amountCents)} USD`;
};

const ALLOCATION_PRODUCTS = [
  {
    afterKey: 'moneyMarket',
    beforeKey: 'moneyMarket',
    label: 'Managed Money Market',
  },
  {
    afterKey: 'tbills',
    beforeKey: 'managedTreasuries',
    label: 'Managed Treasuries',
  },
  {
    afterKey: 'smart',
    beforeKey: 'managedIncome',
    label: 'Managed Income',
  },
  {
    afterKey: 'cash',
    beforeKey: 'cash',
    label: 'Treasure Cash',
  },
] as const;

interface ProductAmounts {
  cash?: number;
  managedIncome?: number;
  managedTreasuries?: number;
  moneyMarket?: number;
}

const formatPercent = (value: number): string => {
  const rounded = Math.round(value * 100) / 100;

  return `${rounded}%`;
};

const formatMix = (
  percentages: Partial<Record<string, number>> | null,
): string | null => {
  if (!percentages) {
    return null;
  }

  return ALLOCATION_PRODUCTS.map(
    (product) =>
      `${product.label} ${formatPercent(percentages[product.label] ?? 0)}`,
  ).join(', ');
};

const percentageOf = (amount: number, total: number): number => {
  if (!Number.isFinite(amount) || !Number.isFinite(total) || total <= 0) {
    return 0;
  }

  return (amount / total) * 100;
};

export const allocationBefore = (
  productAmounts: ProductAmounts | undefined,
): string | null => {
  if (!productAmounts) {
    return null;
  }

  const total = ALLOCATION_PRODUCTS.reduce(
    (sum, product) => sum + (productAmounts[product.beforeKey] ?? 0),
    0,
  );

  if (total <= 0) {
    return 'No current allocation';
  }

  return formatMix(
    Object.fromEntries(
      ALLOCATION_PRODUCTS.map((product) => [
        product.label,
        percentageOf(productAmounts[product.beforeKey] ?? 0, total),
      ]),
    ),
  );
};

export const allocationAfter = (
  payload: ApprovalRequestPayload | null,
): string | null => {
  if (payload?.kind !== 'other') {
    return null;
  }

  const body = payload.body;

  if (!body) {
    return null;
  }

  const slices = ALLOCATION_PRODUCTS.map((product) => body[product.afterKey]);
  const hasPercentage = slices.some(
    (slice) => typeof slice?.percentage === 'number',
  );

  if (!hasPercentage) {
    return null;
  }

  return formatMix(
    Object.fromEntries(
      ALLOCATION_PRODUCTS.map((product) => [
        product.label,
        body[product.afterKey]?.percentage ?? 0,
      ]),
    ),
  );
};

export const withdrawalDestination = (
  payload: ApprovalRequestPayload | null,
  accounts: BankAccount[] | undefined,
): string | undefined => {
  const bankAccountId =
    payload == null || payload.kind === 'other'
      ? undefined
      : payload.body?.bankAccountId;

  if (!bankAccountId || !accounts) {
    return undefined;
  }

  const account = accounts.find((item) => item.Id === bankAccountId);

  if (!account) {
    return undefined;
  }

  const bankName = account.institution?.name || account.Name;
  const mask = account.Mask || account.mask;

  if (bankName && mask) {
    return `${bankName} ${mask}`;
  }

  return bankName || mask || undefined;
};

export const approvalRequestSummary = (
  requestType: string,
  payload: ApprovalRequestPayload | null,
  destination?: string,
): string => {
  switch (requestType) {
    case WITHDRAWAL: {
      const amount = withdrawalAmountLabel(payload);

      return destination ? `${amount} to ${destination}` : amount;
    }
    case REALLOCATION:
      return 'Allocation Change';
    case NEW_ADMIN_MEMBER: {
      const email =
        payload?.kind === 'other' ? payload.body?.person?.email : undefined;

      if (typeof email === 'string' && email.trim() !== '') {
        return `New Admin: ${email.trim()}`;
      }

      return 'New Admin';
    }
    case ENABLE_MULTIPLE_APPROVAL:
      return 'Enable Dual Approval';
    case DISABLE_MULTIPLE_APPROVAL:
      return 'Disable Dual Approval';
    default:
      return requestType;
  }
};

const requesterName = (
  initiatedBy: string,
  members: BusinessMember[] | undefined,
): string => {
  const member = members?.find((item) => item.id === initiatedBy);

  if (!member) {
    return initiatedBy;
  }

  const name = `${member.firstName ?? ''} ${member.lastName ?? ''}`.trim();

  return name || member.email || initiatedBy;
};

const formatExpiresAt = (expiresAt: string): string => {
  const parsed = dayjs(expiresAt);

  return parsed.isValid() ? parsed.format('MMM D, YYYY h:mm A') : expiresAt;
};

interface ApprovalRowProps {
  action: 'approve' | 'cancel';
  allocationBeforeText: string | null;
  destination?: string;
  disabled: boolean;
  onApprove: () => void;
  onCancel: () => void;
  request: ApprovalRequest;
  requester: string;
}

const ApprovalRow: React.FC<ApprovalRowProps> = ({
  action,
  allocationBeforeText,
  destination,
  disabled,
  onApprove,
  onCancel,
  request,
  requester,
}) => {
  const after = allocationAfter(request.payload);
  const showAllocation =
    request.requestType === REALLOCATION && (allocationBeforeText || after);

  return (
    <Box mb={3} pb={3} borderBottom="1px solid var(--color-grey3)">
      <Grid container alignItems="center" spacing={2}>
        <Grid item xs={12} sm={8}>
          <Typography variant={TypographyVariant.SubHeader1}>
            {approvalRequestSummary(
              request.requestType,
              request.payload,
              destination,
            )}
          </Typography>

          {showAllocation && allocationBeforeText && (
            <Typography variant={TypographyVariant.SubHeader3}>
              <strong>Before:</strong> {allocationBeforeText}
            </Typography>
          )}
          {showAllocation && after && (
            <Typography variant={TypographyVariant.SubHeader3}>
              <strong>After:</strong> {after}
            </Typography>
          )}

          <Typography variant={TypographyVariant.SubHeader3}>
            Requested by {requester}
          </Typography>

          <Typography variant={TypographyVariant.SubHeader3}>
            Expires {formatExpiresAt(request.expiresAt)}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Box textAlign="right">
            {action === 'cancel' ? (
              <Button
                disabled={disabled}
                onClick={onCancel}
                type="button"
                variant="outlined"
              >
                Cancel
              </Button>
            ) : (
              <Button disabled={disabled} onClick={onApprove} type="button">
                Approve
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export const Approvals: React.FC = () => {
  useTracking<{
    page: TrackPageName;
  }>({
    page: TrackPageName.SettingsApprovals,
  });

  const { data: business } = useBusiness();
  const { data: currentUser } = useUser(business?.Id);
  const { data: businessMembers } = useBusinessMembers();
  const { data: portfolios } = usePortfolios(business?.Id);
  const { data: bankAccounts } = useBankAccounts(business?.Id);
  const { data, isLoading, isError } = useApprovalRequests();
  const { data: policy, isLoading: policyLoading } = useApprovalPolicy();
  const approveRequest = useApproveApprovalRequest();
  const cancelRequest = useCancelApprovalRequest();

  if (isLoading || policyLoading) {
    return <Spinner />;
  }

  if (isError || !data) {
    return null;
  }

  const dualApprovalOff =
    policy != null &&
    !policy.enabled &&
    policy.pendingRequestType !== ENABLE_MULTIPLE_APPROVAL;

  const disabledBanner = dualApprovalOff ? (
    <Box mb={4}>
      <NotificationContainer>
        <Typography color="grey1" variant={TypographyVariant.Body}>
          Dual approval is off. It can be enabled in{' '}
          <Styled.TeamLink to={RoutesPath.pages.settings.pages.team.path}>
            Team Settings
          </Styled.TeamLink>
          .
        </Typography>
      </NotificationContainer>
    </Box>
  ) : null;

  if (data.items.length === 0) {
    return (
      <>
        {disabledBanner}
        <Typography variant={TypographyVariant.Body}>
          No approvals waiting.
        </Typography>
      </>
    );
  }

  const currentUserId = currentUser?.Id as string | undefined;
  const beforeText = allocationBefore(
    portfolios?.amountsInDollars?.productAmounts,
  );

  const rowIsPending = (requestId: string) =>
    (approveRequest.isLoading &&
      approveRequest.variables?.requestId === requestId) ||
    (cancelRequest.isLoading &&
      cancelRequest.variables?.requestId === requestId);

  return (
    <>
      {disabledBanner}
      {data.items.map((request) => (
        <ApprovalRow
          action={request.initiatedBy === currentUserId ? 'cancel' : 'approve'}
          allocationBeforeText={beforeText}
          destination={withdrawalDestination(request.payload, bankAccounts)}
          disabled={rowIsPending(request.requestId)}
          key={request.requestId}
          onApprove={() =>
            approveRequest.mutate({ requestId: request.requestId })
          }
          onCancel={() =>
            cancelRequest.mutate({ requestId: request.requestId })
          }
          request={request}
          requester={requesterName(request.initiatedBy, businessMembers)}
        />
      ))}
    </>
  );
};
